<template>
  <div>
    <div>

      <!-- isConnectionMinor: facility is part of a connection, but NOT main-facility in connection-->
      <!-- viewOnly: without edit-capabilities -->
      <div v-if="isConnectionMinor || viewOnly">
        <p class="font-weight-bold mb-0">{{ $t('book/technical-info:labels.connection-view') }}:</p>
        <v-card outlined class="mb-0">
          <v-virtual-scroll
              :items="technicalInfo.connection"
              height="200"
              item-height="48"
          >
            <template v-slot:default="{ item, index }">
              <v-list-item v-if="index===0">
                <span>{{$t('book/technical-info:labels.connection-main')}}</span><span class="ml-1 font-weight-bold"> {{ item.name }}</span>
              </v-list-item>
              <v-list-item v-else>
                ⇨ {{ item.name }}
              </v-list-item>
            </template>
          </v-virtual-scroll>
        </v-card>
        <div v-if="!viewOnly" class="mb-8">
          <span>
            <v-icon size="16">info</v-icon>
            <span class="grey--text"> {{$t('book/technical-info:labels.connection-main-remark')}}</span>
          </span>
        </div>
      </div>

      <!-- edit checkbox/autocomplete only visible when not in viewOnly mode
           facility is main-facility in connection group or not part in connection group
           in both cases checkbox for making it Main is shown
           if checkbox is checked - v-autocomplete for connected-facilities is shown
      -->
      <div v-if="!viewOnly && (isConnectionNone || isConnectionMain)">
        <v-checkbox
            v-model="checkConnectionMain"
            :label="$t('book/technical-info:labels.connection-main-check')"
            @change="onCheckChange"
            color="secondary"
            :tabindex="getTabIndex(0)"
        ></v-checkbox>

        <v-autocomplete v-if="checkConnectionMain"
                        :value="value"
                        @input="onAutocompleteInput($event)"
                        outlined
                        :label="$t('book/technical-info:labels.connection-select-facilities')"
                        :menu-props="{ bottom: true, offsetY: true }"
                        :items="selectableFacilities"
                        item-text="name"
                        item-value="id"
                        clearable
                        multiple
                        chips
                        deletable-chips
                        hide-selected
                        hide-details
                        :no-data-text="$t('book/technical-info:labels.connection-no-more-facilities-available')"
                        :search-input.sync="facilitySearch"
                        @change="facilitySearch = ''"
                        class="facility-autocomplete"
                        :tabindex="getTabIndex(1)"
        ></v-autocomplete>
        <div class="mb-8">
          <span>
            <v-icon size="16">info</v-icon>
            <span class="grey--text"> {{$t('book/technical-info:labels.connection-main-remark')}}</span>
          </span>
        </div>
      </div>

      <!-- connection config error information section -->
      <div v-if="isConnectionConfigErrorMainAndMinor">
        <p class="secondary--text">{{$t('book/technical-info:labels.connection-error-main-and-minor')}}</p>
      </div>
      <div v-if="isConnectionConfigErrorPartOfMultiple">
        <p class="secondary--text">{{$t('book/technical-info:labels.connection-error-multiple-main')}}</p>
      </div>
      <div v-if="isConnectionConfigErrorMainAndMinor || isConnectionConfigErrorPartOfMultiple" class="mb-5" >
        <v-card outlined>
          <v-virtual-scroll
              :items="technicalInfo.connectedToMainFacility"
              height="200"
              item-height="48"
          >
            <template v-slot:default="{ item }">
              <v-list-item>
                {{ item.name }}
              </v-list-item>
            </template>
          </v-virtual-scroll>
        </v-card>
        <div v-if="!viewOnly && isConnectionConfigErrorPartOfMultiple" class="mb-3">
          <span>
            <v-icon size="16">info</v-icon>
            <span class="grey--text"> {{$t('book/technical-info:labels.connection-main-remark')}}</span>
          </span>
        </div>

      </div>
    </div>
  </div>
</template>

<script>




import TechnicalInfo from "@/modules/book/data/technical-info";

export default {
  name: "FacilityConnectionView.vue",
  props: {
      value: Array,                 // model-value: selected facilities from connection: TechnicalInfo.connectedFacilities
      technicalInfo: TechnicalInfo,
      selectableFacilities: Array,  // selectable facilities from building for connection - only relevant if viewOnly = false/undefined
      viewOnly: Boolean,    // is view-only-mode  if true, can only
      tabindexStart: Number  // uses 2 tab indices, for checkbox and connected facilities
  },
  data() {
    return {
      facilitySearch: '',
      checkConnectionMain: false,
      temporaryFacilities: [],      // temporary stored selected facilities for storing while check is toggled on and off
    }
  },
  computed: {
    isConnectionNone() {
      if (!this.technicalInfo || !this.technicalInfo.connectedFacilities || !this.technicalInfo.connectedToMainFacility) {
        return false;
      }
      return this.technicalInfo.connectedToMainFacility.length === 0 && this.technicalInfo.connectedFacilities.length === 0;
    },
    isConnectionMain() {
      if (!this.technicalInfo || !this.technicalInfo.connectedFacilities || !this.technicalInfo.connectedToMainFacility) {
        return false;
      }
      return !!this.technicalInfo.connectedFacilities.length && this.technicalInfo.connectedFacilities.length >= 1;
    },
    isConnectionMinor() {
      if (!this.technicalInfo || !this.technicalInfo.connectedFacilities || !this.technicalInfo.connectedToMainFacility) {
        return false;
      }
      return this.technicalInfo.connectedFacilities.length === 0 && !!this.technicalInfo.connectedToMainFacility.length &&
          this.technicalInfo.connectedToMainFacility.length === 1;
    },
    isConnectionConfigErrorPartOfMultiple() {
      if (!this.technicalInfo || !this.technicalInfo.connectedFacilities || !this.technicalInfo.connectedToMainFacility) {
        return false;
      }
      if (this.isConnectionConfigErrorMainAndMinor) {
        return false; // don't show this error, when another more severe error is already triggered.
      }
      return !!this.technicalInfo.connectedToMainFacility.length && this.technicalInfo.connectedToMainFacility.length >= 2;
    },
    isConnectionConfigErrorMainAndMinor() {
      if (!this.technicalInfo || !this.technicalInfo.connectedFacilities || !this.technicalInfo.connectedToMainFacility) {
        return false;
      }
      return !!this.technicalInfo.connectedToMainFacility.length && !!this.technicalInfo.connectedFacilities.length &&
          this.technicalInfo.connectedToMainFacility.length >= 1 &&  this.technicalInfo.connectedFacilities.length >= 1;
    },
  },
  created() {
    this.initCheckBoxAndTemp();
  },
  methods: {
    /**
     *  when checkbox is changed, trigger v-model update with this.$emit('input', value)
     *  internally store previously selected facilities when unchecked, restore when rechecked
     * @param checkValue boolean if is-main-facility in connection
     */
    onCheckChange(checkValue) {
      if (!checkValue) {
        this.temporaryFacilities = [...this.value]; // temporarily store selected facilities when check is unset
        this.$emit('input', []);  // update v-model with non-selected facilities
      }
      if (checkValue) {
        this.$emit('input', this.temporaryFacilities); //  update v-model with restored selected facilities when check is rechecked
      }
    },
    onAutocompleteInput(event) {
      this.$emit('input', event);   // update v-model
    },
    initCheckBoxAndTemp() {
      // default constructor for TechnicalInfo has set facility to null in technical-info.js - so we
      // can check for data-loaded from server by checking technicalInfo.facility !== null
      if (this.technicalInfo && this.technicalInfo.facility !== null) {
        this.temporaryFacilities = [...this.value];
        this.checkConnectionMain = this.isConnectionMain;
      }
    },
    getTabIndex(increment) {
      if (this.tabindexStart) {
        // on error wrong type string for tabindex-start - try to convert it to number
        if ( typeof(this.tabindexStart) === 'string') {
          return Number(this.tabindexStart) + increment;
        }
        return this.tabindexStart + increment;
      }
      return undefined;
    }
  },
  watch: {
    technicalInfo() {
      // in usual cases, TechnicalInfo data is not available when this Component is created
      // So checking on change of Prop TechnicalInfo
      this.initCheckBoxAndTemp();
    },
  }
}
</script>

<style scoped>

</style>